import React, { useState, useEffect } from "react";
import { cn } from "../utils/cn";

type MetricType = "currency" | "percentage" | "score";

interface MetricDeltaProps {
  /** The numeric delta value (positive or negative) */
  delta: number;
  /** How to format the delta */
  type: MetricType;
  /** Optional stagger index for animation delay */
  stagger?: number;
  /** Previous value for tooltip */
  previousValue?: number;
  /** Current value for tooltip */
  currentValue?: number;
  /** Called after the fade-out completes */
  onFadeComplete?: () => void;
}

const FADE_DELAY_MS = 30_000;
const EXIT_DURATION_MS = 500;

function formatDelta(delta: number, type: MetricType): string {
  const sign = delta > 0 ? "+" : "";

  switch (type) {
    case "currency": {
      const formatted = new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.abs(delta));
      return `${sign}${delta < 0 ? "-" : ""}${formatted}`;
    }
    case "percentage":
      return `${sign}${delta}%`;
    case "score":
      return `${sign}${delta} pts`;
  }
}

function formatTooltipValue(value: number, type: MetricType): string {
  switch (type) {
    case "currency":
      return new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    case "percentage":
      return `${value}%`;
    case "score":
      return `${value}`;
  }
}

export const MetricDelta: React.FC<MetricDeltaProps> = ({
  delta,
  type,
  stagger = 0,
  previousValue,
  currentValue,
  onFadeComplete,
}) => {
  const [phase, setPhase] = useState<"entering" | "exiting" | "gone">(
    "entering"
  );

  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      setPhase("exiting");
    }, FADE_DELAY_MS);

    return () => clearTimeout(fadeTimer);
  }, []);

  useEffect(() => {
    if (phase !== "exiting") return;

    const exitTimer = setTimeout(() => {
      setPhase("gone");
      onFadeComplete?.();
    }, EXIT_DURATION_MS);

    return () => clearTimeout(exitTimer);
  }, [phase, onFadeComplete]);

  if (delta === 0 || phase === "gone") {
    return null;
  }

  const isPositive = delta > 0;
  const title =
    previousValue !== undefined && currentValue !== undefined
      ? `Changed from ${formatTooltipValue(previousValue, type)} to ${formatTooltipValue(currentValue, type)}`
      : undefined;

  return (
    <span
      className={cn(
        "metric-delta",
        isPositive ? "metric-delta--positive" : "metric-delta--negative",
        phase === "entering" && "metric-delta--entering",
        phase === "exiting" && "metric-delta--exiting"
      )}
      data-stagger={stagger}
      title={title}
      aria-label={`${isPositive ? "increased" : "decreased"} by ${formatDelta(delta, type)}`}
    >
      <svg
        className="metric-delta-arrow"
        viewBox="0 0 12 12"
        aria-hidden="true"
      >
        {isPositive ? (
          <path d="M6 2L10 7H2L6 2Z" fill="currentColor" />
        ) : (
          <path d="M6 10L2 5H10L6 10Z" fill="currentColor" />
        )}
      </svg>
      <span>{formatDelta(delta, type)}</span>
    </span>
  );
};

MetricDelta.displayName = "MetricDelta";
